import React, { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { client } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Loader2, Lock, CheckCircle2 } from "lucide-react";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Please choose a password of at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Those passwords don't match.");
      return;
    }
    if (!token) {
      setError("This reset link is missing its token. Please request a new one.");
      return;
    }
    setBusy(true);
    try {
      await client.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "This reset link is invalid or has expired. Please request a new one.");
    } finally {
      setBusy(false);
    }
  };

  const inputClass =
    "w-full rounded-xl border border-black/10 bg-white pl-10 pr-4 py-3 text-sm text-brand-navy outline-none focus:border-brand-teal transition-colors";

  return (
    <div className="relative bg-white text-brand-navy min-h-screen grain flex flex-col" data-testid="reset-password-page">
      <header className="border-b border-black/5 px-6 sm:px-10 py-4">
        <Link to="/" className="flex items-center gap-2.5 w-fit">
          <Logo className="w-7 h-7" />
          <span className="font-heading font-extrabold tracking-[0.14em] text-base">NOT BY WILL</span>
        </Link>
      </header>
      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          {done ? (
            <div className="text-center" data-testid="reset-success">
              <div className="rounded-2xl bg-sage w-14 h-14 flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 className="w-7 h-7 text-brand-teal-dark" />
              </div>
              <h1 className="font-heading text-3xl font-semibold mb-3">Password reset</h1>
              <p className="text-stone mb-8">Your password has been updated. You can now sign in with your new password.</p>
              <Link
                to="/"
                data-testid="reset-back-signin"
                className="inline-flex items-center justify-center rounded-xl bg-brand-teal-dark px-6 py-3.5 text-white font-semibold hover:bg-[#0A6F66] transition-colors"
              >
                Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-heading text-3xl sm:text-4xl font-semibold mb-2">Choose a new password</h1>
              <p className="text-stone mb-8">Almost there — set a new password for your account.</p>
              <form onSubmit={submit} className="space-y-3">
                <div className="relative">
                  <Lock className="w-4 h-4 text-stone absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    data-testid="reset-password-input"
                    type="password"
                    autoComplete="new-password"
                    placeholder="New password (8+ characters)"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div className="relative">
                  <Lock className="w-4 h-4 text-stone absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    data-testid="reset-confirm-input"
                    type="password"
                    autoComplete="new-password"
                    placeholder="Confirm new password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className={inputClass}
                  />
                </div>
                {error && (
                  <p data-testid="reset-error" className="text-sm text-red-600">
                    {error}
                  </p>
                )}
                <button
                  data-testid="reset-submit-btn"
                  type="submit"
                  disabled={busy}
                  className="w-full flex items-center justify-center gap-2 rounded-xl bg-brand-teal-dark px-5 py-3.5 text-white font-semibold hover:bg-[#0A6F66] transition-colors disabled:opacity-60"
                >
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />} Reset password
                </button>
              </form>
              <p className="text-center text-sm text-stone mt-6">
                <Link to="/" className="font-semibold text-brand-teal-dark hover:underline">
                  Back to sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
